import moment from "moment";
import { CSSProperties, useCallback, useEffect, useState } from "react";
import { useHistory, useParams } from "react-router-dom";
import Swal from "sweetalert2";
import { IEsteiraDetalhe } from "../../interfaces/IEsteira";
import { ICartaoDetalhe, ILancamento } from "../../interfaces/IRelatorio";
import { Formatter } from "../relatorios/Relatorio";
import cartaoNovaService from "../../services/cartaoNovaService";
import esteiraNovaService from "../../services/esteiraNovaService";
import lancamentoNovaService from "../../services/lancamentoNovaService";
import ExtratoBancarioService, { ExtratoBancarioPendente } from "../../services/ExtratoBancarioService";
import { CardLancamento } from "../conciliacao-inteligente";

type UrlParam = {
    cartaoParam: string,
    esteiraParam: string
}

type FiltroColuna = { ano: string; mes: string; dia: string; historico: string; valor: string };
const filtroVazio: FiltroColuna = { ano: '', mes: '', dia: '', historico: '', valor: '' };

const COR_RAZAO = '#1565c0';
const COR_EXTRATO = '#2e7d32';

const rotuloTipo = (tipo?: string) => tipo === 'C' ? 'Crédito' : 'Débito';

const buildFiltro = (f: FiltroColuna) => {
    const parseIntSafe = (s: string) => { const n = parseInt(s, 10); return isNaN(n) ? undefined : n; };
    const parseValor = (s: string) => {
        const n = parseFloat(s.replace(/\./g, '').replace(',', '.'));
        return isNaN(n) ? undefined : n;
    };
    return {
        ano: f.ano ? parseIntSafe(f.ano) : undefined,
        mes: f.mes ? parseIntSafe(f.mes) : undefined,
        dia: f.dia ? parseIntSafe(f.dia) : undefined,
        historico: f.historico.trim() || undefined,
        valor: f.valor.trim() ? parseValor(f.valor.trim()) : undefined,
    };
};

const colHeader = (color: string): CSSProperties => ({
    background: color,
    color: '#fff',
    borderRadius: '6px 6px 0 0',
    padding: '8px 14px',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    fontWeight: 600,
    fontSize: '14px',
});

function FiltroColunaForm({ filtros, setFiltros, onBuscar, onLimpar, cor }: {
    filtros: FiltroColuna; setFiltros: (f: FiltroColuna) => void;
    onBuscar: () => void; onLimpar: () => void; cor: string;
}) {
    const campo = (nome: keyof FiltroColuna) => ({
        value: filtros[nome],
        onChange: (e: React.ChangeEvent<HTMLInputElement>) => setFiltros({ ...filtros, [nome]: e.target.value }),
        onKeyDown: (e: React.KeyboardEvent) => { if (e.key === 'Enter') onBuscar(); },
    });

    return (
        <div style={{ border: `1px solid ${cor}`, borderTop: 'none', borderBottom: 'none', padding: '8px 10px', background: '#f8f9fa' }}>
            <div style={{ display: 'flex', gap: '6px', marginBottom: '5px' }}>
                <input className="form-control form-control-sm" type="number" placeholder="Ano" {...campo('ano')} style={{ width: '72px' }} />
                <input className="form-control form-control-sm" type="number" placeholder="Mês" min={1} max={12} {...campo('mes')} style={{ width: '60px' }} />
                <input className="form-control form-control-sm" type="number" placeholder="Dia" min={1} max={31} {...campo('dia')} style={{ width: '56px' }} />
            </div>
            <div style={{ display: 'flex', gap: '6px' }}>
                <input className="form-control form-control-sm" type="text" placeholder="Histórico..." {...campo('historico')} style={{ flex: 1 }} />
                <input className="form-control form-control-sm" type="text" placeholder="Valor" {...campo('valor')} style={{ width: '76px' }} />
                <button className="btn btn-sm btn-primary" onClick={onBuscar} style={{ padding: '2px 10px', fontSize: '12px', whiteSpace: 'nowrap' }}>Buscar</button>
                <button className="btn btn-sm btn-outline-secondary" onClick={onLimpar} style={{ padding: '2px 8px', fontSize: '12px' }}>×</button>
            </div>
        </div>
    );
}

function PaginacaoColuna({ page, totalPages, total, onPrev, onNext, loading }: {
    page: number; totalPages: number; total: number;
    onPrev: () => void; onNext: () => void; loading: boolean;
}) {
    return (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '6px 2px', borderTop: '1px solid #e9ecef', marginTop: '6px' }}>
            <button onClick={onPrev} disabled={page <= 1 || loading} className="btn btn-sm btn-outline-secondary" style={{ padding: '2px 10px', fontSize: '12px' }}>‹ Ant.</button>
            <span style={{ fontSize: '12px', color: '#6c757d' }}>
                {loading ? <span className="spinner-border spinner-border-sm" /> : `Pág. ${page} / ${totalPages} (${total} total)`}
            </span>
            <button onClick={onNext} disabled={page >= totalPages || loading} className="btn btn-sm btn-outline-secondary" style={{ padding: '2px 10px', fontSize: '12px' }}>Próx. ›</button>
        </div>
    );
}

// DEVN3-642 — conciliação bancária manual: razão pendente à esquerda, extrato pendente à direita.
// Data, valor e natureza podem divergir; a diferença é exibida e confirmada antes de vincular.
function ConciliacaoBancaria() {
    const { esteiraParam, cartaoParam } = useParams<UrlParam>();
    const history = useHistory();
    const esteiraId = parseInt(esteiraParam) || 0;
    const cartaoId = parseInt(cartaoParam) || 0;

    const [contaAtiva, setContaAtiva] = useState<ICartaoDetalhe>();
    const [esteiraAtiva, setEsteiraAtiva] = useState<IEsteiraDetalhe>();

    const [razao, setRazao] = useState<ILancamento[]>([]);
    const [isLoadingRazao, setIsLoadingRazao] = useState<boolean>(true);
    const [pageRazao, setPageRazao] = useState<number>(1);
    const [totalPagesRazao, setTotalPagesRazao] = useState<number>(1);
    const [totalRazao, setTotalRazao] = useState<number>(0);
    const [filtrosRazao, setFiltrosRazao] = useState<FiltroColuna>(filtroVazio);

    const [extratos, setExtratos] = useState<ExtratoBancarioPendente[]>([]);
    const [isLoadingExtrato, setIsLoadingExtrato] = useState<boolean>(true);
    const [pageExtrato, setPageExtrato] = useState<number>(1);
    const [totalPagesExtrato, setTotalPagesExtrato] = useState<number>(1);
    const [totalExtrato, setTotalExtrato] = useState<number>(0);
    const [filtrosExtrato, setFiltrosExtrato] = useState<FiltroColuna>(filtroVazio);

    // A seleção sobrevive à troca de página e de filtro das colunas.
    const [razaoSelecionados, setRazaoSelecionados] = useState<ILancamento[]>([]);
    const [extratosSelecionados, setExtratosSelecionados] = useState<ExtratoBancarioPendente[]>([]);
    const [vinculando, setVinculando] = useState<boolean>(false);

    const carregarRazao = useCallback((page: number, filtros: FiltroColuna) => {
        setIsLoadingRazao(true);
        lancamentoNovaService.listarPendentes(esteiraId, cartaoId, page, 20, undefined, buildFiltro(filtros))
            .then((resp) => {
                setRazao(resp.items as unknown as ILancamento[]);
                setTotalPagesRazao(resp.totalPages);
                setTotalRazao(resp.total);
                setPageRazao(page);
            })
            .finally(() => setIsLoadingRazao(false));
    }, [esteiraId, cartaoId]);

    const carregarExtratos = useCallback((page: number, filtros: FiltroColuna) => {
        setIsLoadingExtrato(true);
        ExtratoBancarioService.listarPendentes(esteiraId, cartaoId, page, 20, buildFiltro(filtros))
            .then((resp) => {
                setExtratos(resp.items);
                setTotalPagesExtrato(resp.totalPages);
                setTotalExtrato(resp.total);
                setPageExtrato(page);
            })
            .finally(() => setIsLoadingExtrato(false));
    }, [esteiraId, cartaoId]);

    useEffect(() => {
        esteiraNovaService.buscarDetalhe(esteiraId).then(setEsteiraAtiva);
        cartaoNovaService.buscarCartaoPorId(cartaoId).then(setContaAtiva);
        carregarRazao(1, filtroVazio);
        carregarExtratos(1, filtroVazio);
    }, [esteiraId, cartaoId, carregarRazao, carregarExtratos]);

    // Entrada de dinheiro é débito no razão e crédito no extrato (ótica do banco) — os líquidos
    // de cada lado são comparáveis diretamente.
    const liquidoRazao = razaoSelecionados.reduce((acc, l) => acc + (l.valorProvisaoRecolher || 0) * (l.tipoLancamento === 'D' ? 1 : -1), 0);
    const liquidoExtrato = extratosSelecionados.reduce((acc, e) => acc + e.valor * (e.tipo === 'C' ? 1 : -1), 0);
    const diferenca = Math.abs(liquidoRazao - liquidoExtrato);
    const temDiferenca = diferenca >= 0.005;

    const podeVincular = razaoSelecionados.length > 0 && extratosSelecionados.length > 0;

    const vincular = useCallback(async () => {
        if (temDiferenca) {
            const confirmacao = await Swal.fire({
                title: 'Os valores não conferem',
                html: `Razão: <b>${Formatter.format(liquidoRazao)}</b><br/>Extrato: <b>${Formatter.format(liquidoExtrato)}</b><br/>Diferença: <b>${Formatter.format(diferenca)}</b><br/><br/>Deseja vincular mesmo assim?`,
                icon: 'warning',
                showCancelButton: true,
                confirmButtonColor: 'green',
                confirmButtonText: 'Vincular',
                cancelButtonText: 'Cancelar',
            });
            if (!confirmacao.isConfirmed) return;
        }

        setVinculando(true);
        try {
            await ExtratoBancarioService.vincular(
                esteiraId, cartaoId,
                razaoSelecionados.map((l) => l.id).filter((id): id is number => !!id),
                extratosSelecionados.map((e) => e.id));
            Swal.fire({ text: 'Lançamentos vinculados com sucesso.', timer: 2500, icon: 'success', showConfirmButton: false });
        } catch (err: any) {
            const msg = err?.response?.data?.message || err?.message || 'Erro desconhecido';
            Swal.fire({ text: `Não foi possível vincular: ${msg}`, icon: 'error' });
        } finally {
            setRazaoSelecionados([]);
            setExtratosSelecionados([]);
            setVinculando(false);
            carregarRazao(pageRazao, filtrosRazao);
            carregarExtratos(pageExtrato, filtrosExtrato);
        }
    }, [temDiferenca, liquidoRazao, liquidoExtrato, diferenca, esteiraId, cartaoId, razaoSelecionados, extratosSelecionados, carregarRazao, carregarExtratos, pageRazao, pageExtrato, filtrosRazao, filtrosExtrato]);

    if (!contaAtiva || !esteiraAtiva) {
        return (
            <div className="d-flex flex-column align-items-center justify-content-center" style={{ minHeight: '60vh', gap: '16px' }}>
                <div className="spinner-border text-primary" style={{ width: '2.5rem', height: '2.5rem' }} role="status" />
                <span className="text-muted">Carregando lançamentos...</span>
            </div>
        );
    }

    const razaoDisponiveis = razao.filter((l) => !razaoSelecionados.some((s) => s.id === l.id));
    const extratosDisponiveis = extratos.filter((e) => !extratosSelecionados.some((s) => s.id === e.id));

    const corpoColuna = (cor: string, loading: boolean, conteudo: JSX.Element) => (
        <div style={{ flex: 1, maxHeight: '440px', overflowY: 'auto', padding: '10px', border: `1px solid ${cor}`, borderTop: 'none', background: '#fcfcfd' }}>
            {loading ? (
                <div className="d-flex flex-column align-items-center justify-content-center mt-4" style={{ gap: '12px' }}>
                    <div className="spinner-border text-primary" role="status" />
                    <span className="text-muted">Carregando...</span>
                </div>
            ) : conteudo}
        </div>
    );

    return <>
        <div style={{ display: 'flex', flexWrap: 'wrap', background: '#f8f9fa', borderRadius: '8px', border: '1px solid #e9ecef', marginBottom: '14px', overflow: 'hidden' }}>
            {[
                { label: 'Conciliação', value: 'Bancária — Razão × Extrato' },
                { label: 'Período', value: `${esteiraAtiva.periodoInicioString} – ${esteiraAtiva.periodoFimString}` },
                { label: 'Conta', value: `${contaAtiva.contaEmpresa} – ${contaAtiva.descricaoEmpresa}` },
            ].map((item, idx) => (
                <div key={idx} style={{ padding: '10px 20px', borderRight: '1px solid #e9ecef', flex: '1 1 auto' }}>
                    <div style={{ fontSize: '10px', color: '#6c757d', textTransform: 'uppercase', letterSpacing: '0.5px', fontWeight: 600, marginBottom: '2px' }}>{item.label}</div>
                    <div style={{ fontWeight: 600, fontSize: '14px', color: '#343a40' }}>{item.value}</div>
                </div>
            ))}
        </div>

        <div className="row" style={{ marginBottom: '16px' }}>

            {/* Razão */}
            <div className="col-md-4" style={{ display: 'flex', flexDirection: 'column' }}>
                <div style={colHeader(COR_RAZAO)}>
                    <span>Razão — Lançamentos pendentes</span>
                    <span style={{ background: 'rgba(255,255,255,0.25)', borderRadius: '12px', padding: '1px 10px', fontSize: '12px' }}>{totalRazao}</span>
                </div>
                <FiltroColunaForm cor={COR_RAZAO} filtros={filtrosRazao} setFiltros={setFiltrosRazao}
                    onBuscar={() => carregarRazao(1, filtrosRazao)}
                    onLimpar={() => { setFiltrosRazao(filtroVazio); carregarRazao(1, filtroVazio); }} />
                {corpoColuna(COR_RAZAO, isLoadingRazao, <>
                    {razaoDisponiveis.length === 0 && <p className="text-center text-muted mt-3" style={{ fontSize: '13px' }}>Nenhum lançamento pendente no razão.</p>}
                    {razaoDisponiveis.map((item) => (
                        <CardLancamento mostrarRotulo key={`razao-${item.id}`} id={item.id} vinculado={false}
                            tipo={rotuloTipo(item.tipoLancamento)} valor={item.valorProvisaoRecolher || 0} historico={item.historico}
                            data={item.dataLancamento || new Date()}
                            onClick={() => setRazaoSelecionados((old) => [...old, item])} />
                    ))}
                </>)}
                <div style={{ border: `1px solid ${COR_RAZAO}`, borderTop: 'none', borderRadius: '0 0 6px 6px', padding: '6px 10px', background: '#fcfcfd' }}>
                    <PaginacaoColuna page={pageRazao} totalPages={totalPagesRazao} total={totalRazao} loading={isLoadingRazao}
                        onPrev={() => carregarRazao(pageRazao - 1, filtrosRazao)}
                        onNext={() => carregarRazao(pageRazao + 1, filtrosRazao)} />
                </div>
            </div>

            {/* Conciliação indicada */}
            <div className="col-md-4" style={{ display: 'flex', flexDirection: 'column' }}>
                <div style={{ background: '#6f42c1', color: '#fff', borderRadius: '6px 6px 0 0', padding: '8px 14px', fontWeight: 600, fontSize: '14px' }}>
                    Conciliação indicada
                </div>
                <div style={{ flex: 1, maxHeight: '580px', overflowY: 'auto', padding: '10px', border: '1px solid #6f42c1', borderTop: 'none', borderRadius: '0 0 6px 6px', background: '#faf9ff' }}>
                    <div style={{ display: 'flex', gap: '6px', marginBottom: '10px' }}>
                        <div style={{ flex: 1, textAlign: 'center', padding: '6px 4px', background: '#e3f2fd', borderRadius: '4px' }}>
                            <div style={{ fontSize: '10px', color: COR_RAZAO, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.3px' }}>Razão</div>
                            <div style={{ fontSize: '12px', fontWeight: 700, color: COR_RAZAO }}>{Formatter.format(liquidoRazao)}</div>
                        </div>
                        <div style={{ flex: 1, textAlign: 'center', padding: '6px 4px', background: temDiferenca ? '#fff3cd' : '#e9ecef', borderRadius: '4px' }}>
                            <div style={{ fontSize: '10px', color: '#856404', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.3px' }}>Diferença</div>
                            <div style={{ fontSize: '12px', fontWeight: 700, color: '#856404' }}>{Formatter.format(diferenca)}</div>
                        </div>
                        <div style={{ flex: 1, textAlign: 'center', padding: '6px 4px', background: '#e8f5e9', borderRadius: '4px' }}>
                            <div style={{ fontSize: '10px', color: COR_EXTRATO, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.3px' }}>Extrato</div>
                            <div style={{ fontSize: '12px', fontWeight: 700, color: COR_EXTRATO }}>{Formatter.format(liquidoExtrato)}</div>
                        </div>
                    </div>

                    {razaoSelecionados.length > 0 && <div style={{ fontSize: '11px', fontWeight: 700, color: COR_RAZAO, textTransform: 'uppercase', marginBottom: '4px' }}>Razão</div>}
                    {razaoSelecionados.map((item) => (
                        <CardLancamento mostrarRotulo key={`sel-razao-${item.id}`} id={item.id} vinculado
                            tipo={rotuloTipo(item.tipoLancamento)} valor={item.valorProvisaoRecolher || 0} historico={item.historico}
                            data={item.dataLancamento || new Date()}
                            removerClick={() => setRazaoSelecionados((old) => old.filter((s) => s.id !== item.id))} />
                    ))}

                    {extratosSelecionados.length > 0 && <div style={{ fontSize: '11px', fontWeight: 700, color: COR_EXTRATO, textTransform: 'uppercase', margin: '8px 0 4px' }}>Extrato</div>}
                    {extratosSelecionados.map((item) => (
                        <CardLancamento mostrarRotulo key={`sel-extrato-${item.id}`} id={item.id} vinculado
                            tipo={rotuloTipo(item.tipo)} valor={item.valor} historico={item.historico ?? ''}
                            data={item.dataLancamento ? moment(item.dataLancamento).toDate() : new Date()}
                            removerClick={() => setExtratosSelecionados((old) => old.filter((s) => s.id !== item.id))} />
                    ))}

                    {!podeVincular && (
                        <p className="text-center text-muted mt-3" style={{ fontSize: '13px' }}>
                            Selecione ao menos um lançamento do razão e um do extrato.
                        </p>
                    )}
                </div>
            </div>

            {/* Extrato */}
            <div className="col-md-4" style={{ display: 'flex', flexDirection: 'column' }}>
                <div style={colHeader(COR_EXTRATO)}>
                    <span>Extrato — Lançamentos pendentes</span>
                    <span style={{ background: 'rgba(255,255,255,0.25)', borderRadius: '12px', padding: '1px 10px', fontSize: '12px' }}>{totalExtrato}</span>
                </div>
                <FiltroColunaForm cor={COR_EXTRATO} filtros={filtrosExtrato} setFiltros={setFiltrosExtrato}
                    onBuscar={() => carregarExtratos(1, filtrosExtrato)}
                    onLimpar={() => { setFiltrosExtrato(filtroVazio); carregarExtratos(1, filtroVazio); }} />
                {corpoColuna(COR_EXTRATO, isLoadingExtrato, <>
                    {extratosDisponiveis.length === 0 && (
                        <p className="text-center text-muted mt-3" style={{ fontSize: '13px' }}>
                            Nenhum lançamento de extrato pendente. Importe o extrato bancário do cartão.
                        </p>
                    )}
                    {extratosDisponiveis.map((item) => (
                        <CardLancamento mostrarRotulo key={`extrato-${item.id}`} id={item.id} vinculado={false}
                            tipo={rotuloTipo(item.tipo)} valor={item.valor} historico={item.historico ?? ''}
                            data={item.dataLancamento ? moment(item.dataLancamento).toDate() : new Date()}
                            onClick={() => setExtratosSelecionados((old) => [...old, item])} />
                    ))}
                </>)}
                <div style={{ border: `1px solid ${COR_EXTRATO}`, borderTop: 'none', borderRadius: '0 0 6px 6px', padding: '6px 10px', background: '#fcfcfd' }}>
                    <PaginacaoColuna page={pageExtrato} totalPages={totalPagesExtrato} total={totalExtrato} loading={isLoadingExtrato}
                        onPrev={() => carregarExtratos(pageExtrato - 1, filtrosExtrato)}
                        onNext={() => carregarExtratos(pageExtrato + 1, filtrosExtrato)} />
                </div>
            </div>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '12px', paddingBottom: '24px' }}>
            {podeVincular && (
                <button onClick={vincular} className="btn btn-success" type="button" disabled={vinculando}
                    style={{ padding: '8px 28px', fontWeight: 600, borderRadius: '6px', fontSize: '15px' }}>
                    {vinculando ? 'Vinculando...' : 'Vincular'}
                </button>
            )}
            <button type="button" className="btn btn-outline-secondary" disabled={vinculando} onClick={() => history.goBack()}
                style={{ padding: '8px 24px', fontWeight: 600, borderRadius: '6px' }}>
                Concluir conciliação
            </button>
        </div>
    </>;
}

export default ConciliacaoBancaria;
